import firebase from "firebase/app";
import phoneFormat from "phone";
import { useMemo, useState, useContext, useEffect } from "react";
import { PaymentMethod, StripeCardElement } from "@stripe/stripe-js";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import parser from "query-string";
import useSegment from "../utils/useSegment";
import ROUTES from "../utils/routes";
import { useForm } from "../utils";
import { registrationValidation } from "./Validation/registrationValidation";
import { userStore } from "../utils/UserContext";
import { algoliaStore } from "../Algolia";
import { TRIAL, CHALLENGE } from "../utils/stripePrices";
import { AppState } from "../../redux/root-reducer";
import { AffiliateTrackState } from "../../redux/affiliate-track/types";

const useRegisterHandler = () => {
  const form = useForm({ shouldUnregister: false, mode: "onBlur" });
  const validation = useMemo(() => registrationValidation, []);

  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const { redirect, plan } = parser.parse(search);

  const { values } = form;
  const { identify } = useSegment();
  const { forceUpdate } = useContext(algoliaStore);
  const { user, refresh } = useContext(userStore);
  const { affiliate } = useSelector<AppState, AffiliateTrackState>(
    (state) => state.affiliate
  );

  const [formError, setFormError] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");

  const emailExists = firebase.functions().httpsCallable("emailExists");
  const attachPaymentMethod = firebase
    .functions()
    .httpsCallable("attachPaymentMethod");
  const createCustomer = firebase.functions().httpsCallable("createCustomer");
  const startSubscription = firebase
    .functions()
    .httpsCallable("startSubscription");
  const updateLoggedInUser = firebase
    .functions()
    .httpsCallable("updateLoggedInUser");
  const createAuthUser = firebase.functions().httpsCallable("createAuthUser");

  const createUser = async () => {
    // If user exists, return early (step already completed)
    if (firebase.auth().currentUser?.uid) {
      return;
    }

    if (!values.emailAddress || !values.password) {
      throw new Error("Email or password is invalid.");
    }

    setLoadingMessage("Creating User");

    await createAuthUser({
      email: values.emailAddress,
      password: values.password,
    });
    await firebase
      .auth()
      .signInWithEmailAndPassword(values.emailAddress, values.password);

    setLoadingMessage("Building Customer Profile");

    await updateLoggedInUser({
      facebookURL: values.facebookUrl,
      facebookEmail: values.facebookEmail,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      phone: values.optIn ? phoneFormat(values.phoneNumber)[0] : "",
      instagramURL: values.instagramHandle,
      displayName: `${values.firstName} ${values.lastName}`,
      email: values.emailAddress,
      createdAt: firebase.firestore.Timestamp.now(),
    });
  };

  const attachPaymentMethodWrapper = async (paymentMethod?: PaymentMethod) => {
    if (!paymentMethod) {
      throw new Error("Missing payment method to attach.");
    }

    // Attach the card to the customer in Stripe
    const method = await attachPaymentMethod({
      paymentMethodId: paymentMethod.id,
    });

    if (!method) {
      throw new Error("Error attaching this credit card.");
    }
  };

  const createPaymentProfile = async () => {
    setLoadingMessage("Creating Payment Profile");

    // Create the stripe customer
    await createCustomer({});
  };

  const processPaymentMethod = async () => {
    if (!elements || !stripe) {
      throw new Error("There was an error loading the payment client.");
    }

    const cardElement = elements.getElement(CardElement);
    setLoadingMessage("Attaching Card to Account");

    // Create the payment method in Stripe
    const {
      error: paymentError,
      paymentMethod,
    } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
    });

    if (paymentError) {
      setLoadingMessage("");
      throw paymentError.message as string;
    }

    return paymentMethod || undefined;
  };

  const startSubscriptionWrapper = async () => {
    setLoadingMessage("Starting Subscription");

    // Start subscription
    const subscription = await startSubscription({
      subscriptionInterval: values.subscription.id,
      price: values.subscription.salePrice || values.subscription.price,
      couponID: values.subscription?.plan?.sale?.id,
      isChallenge: plan === CHALLENGE,
      isNew: true,
      affiliate,
    });
    if (!subscription) {
      throw new Error("Error creating subscription.");
    }
  };

  const registerUser = async () => {
    try {
      setFormError("");
      const subscriptionId = form.values?.subscription?.id;
      const isTrialUser =
        subscriptionId === TRIAL || subscriptionId === CHALLENGE;

      await createUser();
      await createPaymentProfile();

      if (!isTrialUser) {
        const paymentMethod = await processPaymentMethod();
        await attachPaymentMethodWrapper(paymentMethod);
      }

      await startSubscriptionWrapper();

      setLoadingMessage("Complete");

      await refresh();

      forceUpdate();

      // Send conversion to GTM
      window.dataLayer?.push({
        event: isTrialUser ? "User Started Free Trial" : "User Signed Up",
        conversionValue: values.subscription.price,
      });

      if (plan === CHALLENGE) {
        window.dataLayer?.push({ event: "Joined Challenge" });
      }

      // enroll new user in sms campaign if opted in
      if (values.optIn) {
        identify({
          id: firebase.auth().currentUser?.uid,
          data: { SC_SMS_WELCOME_SUBSCRIPTION: "Yes" },
          options: {
            integrations: {
              All: false,
              ActiveCampaign: true,
            },
          },
        });
      }

      // Allows for the completion of the registration animation
      setTimeout(() => {
        history.push({ pathname: ROUTES.dashboard.home, state: { redirect } });
      }, 1000);
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleRegister = async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    await registerUser();
  };

  const checkIfEmailExists = async (email: string) => {
    const signInMethods = await firebase
      .auth()
      .fetchSignInMethodsForEmail(email);
    const emailResult = await emailExists({ email });
    const { emailFound } = emailResult.data;

    if (signInMethods.length || emailFound) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    // prevents awkward redirect during signup
    if (loadingMessage) {
      return;
    }

    // if expired trial user
    // set subscription select page as landing page
    // set state for expired CTA modal to display
    const isExpiredTrialUser =
      user?.hasTrialed && user?.stripeSubscriptionStatus === "Inactive";
    if (user?.id && isExpiredTrialUser) {
      history.push(ROUTES.user.settings.plan, {
        from: pathname,
        expiredTrialUser: isExpiredTrialUser,
      });
      return;
    }

    if (user?.id) {
      history.push(ROUTES.dashboard.home);
    }
  }, [user]);

  return {
    form,
    validation,
    loadingMessage,
    formError,
    actions: {
      checkIfEmailExists,
      handleRegister,
    },
  };
};

export default useRegisterHandler;
